import type { AgentBillingAddPublishedElementsModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { SplitModal } from "@replohq/lattice/blocks/modal/SplitModal";
import { toast } from "@replohq/lattice/components/toast/Toast";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useModal } from "@v2/store/modalStore";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { formatNumber } from "replo-utils/lib/format";
import { pluralize } from "replo-utils/lib/pluralize";

import { showAgentBillingUpdateSuccessToast } from "../agentBillingToasts";
import AddSubscriptionItemContent from "../components/AddSubscriptionItemContent";
import useAgentApplySubscriptionChangeMutation from "../hooks/useAgentApplySubscriptionChangeMutation";
import { useAgentBillingSubscription } from "../hooks/useAgentSubscription";
import { useRateCards } from "../hooks/useRateCards";

const AgentBillingAddPublishedElementsModal: React.FC<
  AgentBillingAddPublishedElementsModalProps
> = () => {
  const workspace = useCurrentWorkspace();
  const modal = useModal();
  const { data: activeSubscription, currentDraft } =
    useAgentBillingSubscription();
  const { rateCards } = useRateCards();
  const { data: publishedElementsUsage } =
    workspaceRouter.getPublishedElementsUsage.useQuery(
      workspace?.id ?? skipToken,
    );

  const { applySubscriptionChange, isLoading: isLoadingSubscriptionUpdate } =
    useAgentApplySubscriptionChangeMutation();

  if (
    !workspace ||
    !activeSubscription ||
    !rateCards ||
    !currentDraft ||
    !publishedElementsUsage ||
    publishedElementsUsage.maximum === null ||
    !activeSubscription.currentPeriodEnd ||
    // (yuxin, 2026-07-14, REPL-29329): custom-contract add-ons go through support.
    activeSubscription.hasCustomPricing
  ) {
    return null;
  }

  const rateCard = rateCards.find(
    (rateCard) => rateCard.plan === activeSubscription.plan,
  );
  const publishedElementsProduct = rateCard?.addOnProducts.find((product) => {
    return product.featureType === "shopifyThemeBuilder.publishedElements";
  });

  const closeModal = () => {
    modal.closeModal({ type: "agentBillingAddPublishedElementsModal" });
  };

  const productDisplayName =
    publishedElementsProduct?.displayName ?? "Published Element";

  const maxAdditionalPublishedElements =
    publishedElementsProduct?.maxProductCount !== null &&
    publishedElementsProduct?.maxProductCount !== undefined
      ? publishedElementsProduct.maxProductCount -
        publishedElementsProduct.currentProductCount
      : undefined;

  const modalGraphicSrc = "/images/billing/published-items-modal-graphic.png";

  const content = publishedElementsProduct ? (
    <AddSubscriptionItemContent
      currentSubscriptionDraft={currentDraft}
      product={publishedElementsProduct}
      currentCount={publishedElementsUsage.maximum}
      interval={activeSubscription.interval}
      currentPeriodEnd={activeSubscription.currentPeriodEnd}
      rateCards={rateCards}
      title="Add Published Items"
      maxQuantity={maxAdditionalPublishedElements}
      onConfirm={async ({ subscriptionView, additionalCount }) => {
        try {
          analytics.logEvent("agentBilling.addPublishedElements.submitted", {
            currentPlan: activeSubscription.plan,
            additionalCount,
          });

          await applySubscriptionChange({
            workspaceId: workspace.id,
            subscriptionView,
            successUrl: window.location.href,
            cancelUrl: window.location.href,
          });

          showAgentBillingUpdateSuccessToast(
            `Added ${formatNumber(additionalCount)} additional ${pluralize(productDisplayName, additionalCount)}`,
            { showConfetti: true },
          );
          closeModal();
        } catch (error) {
          const displayName = pluralize(productDisplayName, 2);
          const errorMessage =
            error instanceof Error
              ? error.message
              : `Failed to add ${displayName}`;
          toast.error({
            title: `Failed to add ${displayName}`,
            description: errorMessage,
          });
        }
      }}
      isLoading={isLoadingSubscriptionUpdate}
    />
  ) : null;

  return (
    <SplitModal
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
      media={
        <img
          src={modalGraphicSrc}
          alt=""
          className="h-full w-full object-cover"
        />
      }
    >
      {content}
    </SplitModal>
  );
};

export default AgentBillingAddPublishedElementsModal;
